import api from './api';

// Talks to /api/labour/* — the Variable Labour shift planner shared by
// Distribution and Manufacturing. Version and site come from the
// x-budget-version-id / x-site-code headers (set by the api interceptor); the
// MODULE is passed explicitly, because one page serves both modules.
export const labourService = {
  // { functions, costCenters, glAccounts }
  getFunctions: async (moduleCode) => {
    const res = await api.get('/labour/functions', { params: { module: moduleCode } });
    return res.data;
  },

  // fn: { function_code, function_name, cost_center_code, gl_account_code, sort_order, notes }
  saveFunction: async (moduleCode, fn) => {
    const res = await api.put('/labour/functions', { ...fn, module_code: moduleCode });
    return res.data;
  },

  deleteFunction: async (moduleCode, functionCode) => {
    const res = await api.delete('/labour/functions', {
      data: { module_code: moduleCode, function_code: functionCode },
    });
    return res.data;
  },

  // { shifts: [{ shift_no, shift_name, is_active, notes }] } — the named crews
  getShifts: async (moduleCode, functionCode) => {
    const res = await api.get('/labour/shifts', { params: { module: moduleCode, function: functionCode } });
    return res.data;
  },

  saveShifts: async (moduleCode, functionCode, shifts) => {
    const res = await api.put('/labour/shifts', {
      module_code: moduleCode, function_code: functionCode, shifts,
    });
    return res.data;
  },

  deleteShift: async (moduleCode, functionCode, shiftNo) => {
    const res = await api.delete('/labour/shifts', {
      data: { module_code: moduleCode, function_code: functionCode, shift_no: shiftNo },
    });
    return res.data;
  },

  // Every function + shift for the version, across ALL modules — what People >
  // Structures needs for its pickers. Honours the site header, or spans every
  // site when none is selected ("All sites").
  getCatalog: async () => {
    const res = await api.get('/labour/catalog');
    return res.data;
  },

  // { financialYear, plan, slots, calendar }
  getPlan: async (moduleCode, functionCode) => {
    const res = await api.get('/labour/plan', { params: { module: moduleCode, function: functionCode } });
    return res.data;
  },

  // months: [{ financial_period, shifts_allowed, block_hours, assign_mode,
  //            alternating, rotation_weeks, slots: [...], calendar: [...] }]
  // deferCalc leaves the hours/cost recalculation to a following
  // recalculate() call, so the caller can show which stage it is on. Without
  // it the save recalculates server-side and returns `summary` itself.
  savePlan: async (moduleCode, functionCode, months, { deferCalc = false } = {}) => {
    const res = await api.put('/labour/plan', {
      module_code: moduleCode,
      function_code: functionCode,
      months,
      defer_calc: deferCalc,
    });
    return res.data;
  },

  // { shifts: [{ shift_no, shift_name, headcount, hours, paid_equiv_hours,
  //              base_hours, premium_hours,
  //              base_cost, overtime_cost, allowance_cost, total_cost }],
  //   total: { ...the same figures summed over every crew } }
  // savePlan already returns this under `summary` for the function it saved.
  getSummary: async (moduleCode, functionCode) => {
    const res = await api.get('/labour/summary', { params: { module: moduleCode, function: functionCode } });
    return res.data;
  },

  // ---- Optimise -----------------------------------------------------------
  // The numbers the planner is PROMPTED with before being asked for a target:
  // expected volume per period on two bases (production / dispatch, units /
  // pallets), what the plan delivers today, and therefore the hours-per-unit
  // rate it is running at. Read-only.
  // { defaultSource, volumes, current, implied, crews, rulesFound, ... }
  getOptimiseContext: async (moduleCode, functionCode) => {
    const res = await api.get('/labour/optimise/context', {
      params: { module: moduleCode, function: functionCode },
    });
    return res.data;
  },

  // Propose a roster. Volume x the rate you give = the hours it aims at.
  // The engine is fed pay server-side so it can rank equally-good rosters by
  // what they cost, but NO money comes back — `premiumPct` (the gap between
  // worked and paid hours) is what stands in for it.
  // payload: { financial_period, source, basis, worked_hours_per_unit?, paid_hours_per_unit? }
  optimise: async (moduleCode, functionCode, payload) => {
    const res = await api.post('/labour/optimise', {
      module_code: moduleCode, function_code: functionCode, ...payload,
    });
    return res.data;
  },

  // Re-run the hours + cost calculation without touching the plan — for when
  // pay or the overtime rules changed rather than the roster.
  recalculate: async (moduleCode, functionCode) => {
    const res = await api.post('/labour/recalculate', {
      module_code: moduleCode, function_code: functionCode,
    });
    return res.data;
  },
};

export default labourService;
